import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  HiOutlineTicket,
  HiOutlineClipboardDocument,
  HiCheck,
  HiOutlineTrash,
} from "react-icons/hi2";
import { IoClose } from "react-icons/io5";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import PrimaryButton from "../components/PrimaryButton";

const DISCOUNT_TYPES = [
  { label: "Percentage (%)", value: "percentage" },
  { label: "Fixed amount", value: "fixed" },
];

const DURATIONS = [
  { label: "Once", value: "once" },
  { label: "Repeating", value: "repeating" },
  { label: "Forever", value: "forever" },
];

const PLANSALLOW = [
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
  { label: "Both Monthly and Yearly", value: "both" },
];

const CURRENCIES = [
  { label: "USD ($)", value: "usd" },
  { label: "EUR (€)", value: "eur" },
  { label: "GBP (£)", value: "gbp" },
  { label: "CAD ($)", value: "cad" },
  { label: "AUD ($)", value: "aud" },
];

const INITIAL_FORM = {
  name: "",
  promotionCode: "",
  discountType: "percentage",
  discountValue: "",
  currency: "usd",
  duration: "once",
  allowthisCoupon: "both",
  durationInMonths: "",
  maxRedemptions: "",
  redeemBy: "",
};

const STATUS_COLORS = {
  active: { bg: "#e6f4ea", color: "#1e7e34" },
  inactive: { bg: "#f1f3f5", color: "#6c757d" },
  expired: { bg: "#fdecea", color: "#c0392b" },
};

const CURRENCY_SYMBOLS = { usd: "$", eur: "€", gbp: "£", cad: "$", aud: "$" };

// Native-field styling. Plain <input>/<select> elements styled so every field
// — text, number, select and date — is the same width and height. Focus/hover
// states live in the <style> block below (inline styles can't target :focus).
const CHEVRON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E";

const FIELD_CSS = `
  .cpn-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 8px; }
  .cpn-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
  .cpn-label { font-size: 12.5px; font-weight: 600; color: #374151; }
  .cpn-control { position: relative; display: flex; align-items: center; }
  .cpn-input, .cpn-select {
    width: 100%; height: 44px; box-sizing: border-box;
    border: 1px solid #e5e7eb; border-radius: 10px; background: #fff;
    padding: 0 12px; font-size: 14px; color: #1f2937; outline: none;
    transition: box-shadow .15s ease, border-color .15s ease;
  }
  .cpn-select {
    appearance: none; -webkit-appearance: none; -moz-appearance: none; cursor: pointer;
    padding-right: 34px;
    background-image: url("${CHEVRON}");
    background-repeat: no-repeat; background-position: right 12px center;
  }
  .cpn-input::placeholder { color: #9ca3af; }
  .cpn-input:hover, .cpn-select:hover { border-color: #c7d2fe; }
  .cpn-input:focus, .cpn-select:focus {
    border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }
  .cpn-input.cpn-err, .cpn-select.cpn-err { border-color: #dc2626; }
  .cpn-affix { position: absolute; color: #6b7280; font-size: 14px; pointer-events: none; }
  .cpn-affix.start { left: 12px; }
  .cpn-affix.end { right: 12px; }
  .cpn-help { font-size: 11.5px; color: #6b7280; }
  .cpn-help.cpn-err { color: #dc2626; }
  @media (max-width: 600px) { .cpn-grid { grid-template-columns: 1fr; } }
`;

function Coupens() {
  const [agency] = useAgencyInfo();
  const AppService = useAppServices();

  const [coupens, setCoupens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const getCoupens = async () => {
    setLoading(true);
    const { response } = await AppService.saas.GetCoupens({
      query: `agency_id=${agency._id}`,
      toaster: false,
    });
    setCoupens(response?.data || []);
    setLoading(false);
  };

  useEffect(() => {
    getCoupens();
  }, []);

  const handleOpen = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setOpen(true);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleCopy = async (coupen) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(coupen.code);
      } else {
        const el = document.createElement("textarea");
        el.value = coupen.code;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopiedId(coupen._id);
      setTimeout(() => setCopiedId((id) => (id === coupen._id ? null : id)), 1500);
    } catch (err) {
      // clipboard not available — silently ignore
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeletingId(confirmDelete._id);
    const { response } = await AppService.saas.DeleteCoupen({
      query: `_id=${confirmDelete._id}&agency_id=${agency._id}`,
      toaster: true,
    });
    setDeletingId(null);
    if (response?.success) {
      setConfirmDelete(null);
      getCoupens();
    }
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.promotionCode.trim()) next.promotionCode = "Promotion code is required";
    if (form.discountValue === "" || Number(form.discountValue) <= 0)
      next.discountValue = "Enter a valid discount value";
    if (form.discountType === "percentage" && Number(form.discountValue) > 100)
      next.discountValue = "Percentage cannot exceed 100";
    if (form.discountType === "fixed" && !form.currency)
      next.currency = "Currency is required for fixed discounts";
    if (
      form.duration === "repeating" &&
      (!form.durationInMonths || Number(form.durationInMonths) <= 0)
    )
      next.durationInMonths = "Months required for repeating coupons";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);

    const payload = {
      agency_id: agency._id,
      name: form.name.trim(),
      promotionCode: form.promotionCode.trim().toUpperCase(),
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      duration: form.duration,
      allowthisCoupon: form.allowthisCoupon,
    };

    if (form.discountType === "fixed") payload.currency = form.currency;
    if (form.duration === "repeating") payload.durationInMonths = Number(form.durationInMonths);
    if (form.maxRedemptions) payload.maxRedemptions = Number(form.maxRedemptions);
    // Stripe expects redeem_by as a unix timestamp (seconds)
    if (form.redeemBy) payload.redeemBy = Math.floor(new Date(form.redeemBy).getTime() / 1000);

    const { response } = await AppService.saas.CreateCoupen({
      payload,
      toaster: true,
    });

    setSubmitting(false);

    // On HTTP 400 the hook returns { error } and already surfaced the toast.
    if (response?.success) {
      setOpen(false);
      getCoupens();
    }
  };

  const formatDiscount = (coupen) => {
    if (coupen.discountType === "percentage") return `${coupen.discountValue}% off`;
    const symbol = (coupen.currency || "").toUpperCase();
    return `${coupen.discountValue} ${symbol} off`;
  };

  const formatDuration = (coupen) => {
    if (coupen.duration === "repeating") return `Repeating · ${coupen.durationInMonths || 0} mo`;
    if (coupen.duration === "forever") return "Forever";
    return "Once";
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString();
  };

  const previewDiscount = useMemo(() => {
    const value = form.discountValue;
    if (!value || Number(value) <= 0) return "Discount preview";
    if (form.discountType === "percentage") return `${value}% off`;
    const symbol = CURRENCY_SYMBOLS[form.currency] || "$";
    return `${symbol}${value} off`;
  }, [form.discountValue, form.discountType, form.currency]);

  const styles = useMemo(
    () => ({
      page: { padding: "8px 4px 24px" },
      header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        marginBottom: 20,
      },
      heading: { fontSize: 18, fontWeight: 700, color: "#1f2937", margin: 0 },
      subHeading: { fontSize: 13, color: "#6b7280", marginTop: 4 },
      card: {
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
      },
      tableWrap: { width: "100%", overflowX: "auto" },
      table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5 },
      th: {
        textAlign: "left",
        padding: "14px 18px",
        fontWeight: 600,
        color: "#6b7280",
        textTransform: "uppercase",
        fontSize: 11,
        letterSpacing: "0.04em",
        background: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
        whiteSpace: "nowrap",
      },
      td: {
        padding: "14px 18px",
        borderBottom: "1px solid #f1f3f5",
        color: "#374151",
        whiteSpace: "nowrap",
      },
      sectionLabel: {
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "#9ca3af",
        marginBottom: 12,
      },
      codeCell: { display: "inline-flex", alignItems: "center", gap: 6 },
      codePill: {
        display: "inline-block",
        fontFamily: "monospace",
        fontWeight: 700,
        background: "#eef2ff",
        color: "#3730a3",
        padding: "4px 10px",
        borderRadius: 6,
        letterSpacing: "0.05em",
      },
      empty: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "56px 20px",
        color: "#9ca3af",
        gap: 10,
      },
      loaderWrap: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "56px 20px",
      },
    }),
    []
  );

  const StatusBadge = ({ status }) => {
    const c = STATUS_COLORS[status] || STATUS_COLORS.inactive;
    return (
      <span
        style={{
          background: c.bg,
          color: c.color,
          padding: "3px 10px",
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 600,
          textTransform: "capitalize",
        }}
      >
        {status || "—"}
      </span>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.heading}>Coupons</h3>
          <div style={styles.subHeading}>Create and manage discount coupons for your plans.</div>
        </div>
        <PrimaryButton showPlus type="button" onClick={handleOpen}>
          Create Coupon
        </PrimaryButton>
      </div>

      <div style={styles.card}>
        {loading ? (
          <div style={styles.loaderWrap}>
            <CircularProgress size={28} sx={{ color: "#3730a3" }} />
          </div>
        ) : coupens.length === 0 ? (
          <div style={styles.empty}>
            <HiOutlineTicket size={40} />
            <div style={{ fontWeight: 600, color: "#6b7280" }}>No coupons yet</div>
            <div style={{ fontSize: 13 }}>Click “Create Coupon” to add your first discount.</div>
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Discount</th>
                  <th style={styles.th}>Duration</th>
                  <th style={styles.th}>Redemptions</th>
                  <th style={styles.th}>Expires</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Allow this coupon?</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupens.map((coupen) => (
                  <tr key={coupen._id}>
                    <td style={styles.td}>
                      <span style={styles.codeCell}>
                        <span style={styles.codePill}>{coupen.code}</span>
                        <Tooltip
                          title={copiedId === coupen._id ? "Copied!" : "Copy code"}
                          placement="top"
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(coupen)}
                            sx={{ color: copiedId === coupen._id ? "#1e7e34" : "#6b7280" }}
                          >
                            {copiedId === coupen._id ? (
                              <HiCheck size={16} />
                            ) : (
                              <HiOutlineClipboardDocument size={16} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </span>
                    </td>
                    <td style={styles.td}>{coupen.name}</td>
                    <td style={styles.td}>{formatDiscount(coupen)}</td>
                    <td style={styles.td}>{formatDuration(coupen)}</td>
                    <td style={styles.td}>
                      {coupen.timesRedeemed || 0}
                      {coupen.maxRedemptions ? ` / ${coupen.maxRedemptions}` : ""}
                    </td>
                    <td style={styles.td}>{formatDate(coupen.redeemBy)}</td>
                    <td style={styles.td}>
                      <StatusBadge status={coupen.status} />
                    </td>
                    <td style={styles.td}>{coupen.allowthisCoupon || "N/A"}</td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      <Tooltip title="Delete coupon" placement="top" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(coupen)}
                            disabled={deletingId === coupen._id}
                            sx={{
                              color: "#dc2626",
                              "&:hover": { background: "rgba(220,38,38,0.08)" },
                            }}
                          >
                            {deletingId === coupen._id ? (
                              <CircularProgress size={16} sx={{ color: "#dc2626" }} />
                            ) : (
                              <HiOutlineTrash size={17} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(16,24,40,0.22)",
          },
        }}
      >
        {/* Gradient header */}
        <div
          style={{
            position: "relative",
            padding: "22px 24px",
            background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 14,
          }}
        >
          <span
            style={{
              width: 46,
              height: 46,
              borderRadius: 12,
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HiOutlineTicket size={24} />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>Create Coupon</div>
            <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>
              Offer a discount your customers can redeem at checkout.
            </div>
          </div>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={submitting}
            sx={{
              color: "#fff",
              background: "rgba(255,255,255,0.12)",
              "&:hover": { background: "rgba(255,255,255,0.24)" },
            }}
          >
            <IoClose />
          </IconButton>
        </div>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, background: "#fafafe", overflow: "auto", maxHeight: "70vh" }}>
            {/* Live preview */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: "16px 18px",
                marginBottom: 22,
                borderRadius: 14,
                background: "#fff",
                border: "1px dashed #c7d2fe",
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
              }}
            >
              <div
                style={{
                  fontFamily: "monospace",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  fontSize: 16,
                  color: "#3730a3",
                  background: "#eef2ff",
                  padding: "8px 14px",
                  borderRadius: 8,
                  minWidth: 110,
                  textAlign: "center",
                }}
              >
                {form.promotionCode ? form.promotionCode.toUpperCase() : "CODE"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#1f2937", fontSize: 14 }}>
                  {form.name || "Coupon name"}
                </div>
                <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 2 }}>
                  {previewDiscount} · {formatDuration(form)}
                </div>
              </div>
            </div>

            <style>{FIELD_CSS}</style>

            {/* Section: Details */}
            <div style={styles.sectionLabel}>Coupon details</div>
            <div className="cpn-grid">
              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-name">
                  Coupon name
                </label>
                <input
                  id="cpn-name"
                  className={`cpn-input${errors.name ? " cpn-err" : ""}`}
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Summer Sale"
                />
                <span className={`cpn-help${errors.name ? " cpn-err" : ""}`}>
                  {errors.name || " "}
                </span>
              </div>
              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-code">
                  Promotion code
                </label>
                <input
                  id="cpn-code"
                  className={`cpn-input${errors.promotionCode ? " cpn-err" : ""}`}
                  name="promotionCode"
                  value={form.promotionCode}
                  onChange={handleChange}
                  placeholder="SUMMER20"
                  style={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                />
                <span className={`cpn-help${errors.promotionCode ? " cpn-err" : ""}`}>
                  {errors.promotionCode || "Customers enter this at checkout"}
                </span>
              </div>
            </div>

            {/* Section: Discount */}
            <div style={styles.sectionLabel}>Discount</div>
            <div className="cpn-grid">
              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-type">
                  Discount type
                </label>
                <select
                  id="cpn-type"
                  className="cpn-select"
                  name="discountType"
                  value={form.discountType}
                  onChange={handleChange}
                >
                  {DISCOUNT_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span className="cpn-help">&nbsp;</span>
              </div>

              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-value">
                  {form.discountType === "percentage" ? "Percent off" : "Amount off"}
                </label>
                <div className="cpn-control">
                  {form.discountType === "fixed" && (
                    <span className="cpn-affix start">
                      {CURRENCY_SYMBOLS[form.currency] || "$"}
                    </span>
                  )}
                  <input
                    id="cpn-value"
                    type="number"
                    min={0}
                    className={`cpn-input${errors.discountValue ? " cpn-err" : ""}`}
                    name="discountValue"
                    value={form.discountValue}
                    onChange={handleChange}
                    style={{
                      paddingLeft: form.discountType === "fixed" ? 26 : undefined,
                      paddingRight: form.discountType === "percentage" ? 26 : undefined,
                    }}
                  />
                  {form.discountType === "percentage" && <span className="cpn-affix end">%</span>}
                </div>
                <span className={`cpn-help${errors.discountValue ? " cpn-err" : ""}`}>
                  {errors.discountValue || " "}
                </span>
              </div>

              {form.discountType === "fixed" && (
                <div className="cpn-field">
                  <label className="cpn-label" htmlFor="cpn-currency">
                    Currency
                  </label>
                  <select
                    id="cpn-currency"
                    className={`cpn-select${errors.currency ? " cpn-err" : ""}`}
                    name="currency"
                    value={form.currency}
                    onChange={handleChange}
                  >
                    {CURRENCIES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <span className={`cpn-help${errors.currency ? " cpn-err" : ""}`}>
                    {errors.currency || " "}
                  </span>
                </div>
              )}

              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-duration">
                  Duration
                </label>
                <select
                  id="cpn-duration"
                  className="cpn-select"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                >
                  {DURATIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span className="cpn-help">&nbsp;</span>
              </div>

              {form.duration === "repeating" && (
                <div className="cpn-field">
                  <label className="cpn-label" htmlFor="cpn-months">
                    Duration in months
                  </label>
                  <input
                    id="cpn-months"
                    type="number"
                    min={1}
                    className={`cpn-input${errors.durationInMonths ? " cpn-err" : ""}`}
                    name="durationInMonths"
                    value={form.durationInMonths}
                    onChange={handleChange}
                  />
                  <span className={`cpn-help${errors.durationInMonths ? " cpn-err" : ""}`}>
                    {errors.durationInMonths || " "}
                  </span>
                </div>
              )}

              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-duration">
                  Select Allow Plans
                </label>
                <select
                  id="cpn-duration"
                  className="cpn-select"
                  name="allowthisCoupon"
                  value={form.allowthisCoupon}
                  onChange={handleChange}
                >
                  {PLANSALLOW.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <span className="cpn-help">&nbsp;</span>
              </div>
            </div>

            {/* Section: Restrictions */}
            <div style={styles.sectionLabel}>Restrictions (optional)</div>
            <div className="cpn-grid">
              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-max">
                  Max redemptions
                </label>
                <input
                  id="cpn-max"
                  type="number"
                  min={1}
                  className="cpn-input"
                  name="maxRedemptions"
                  value={form.maxRedemptions}
                  onChange={handleChange}
                  placeholder="Unlimited"
                />
                <span className="cpn-help">Leave empty for unlimited</span>
              </div>
              <div className="cpn-field">
                <label className="cpn-label" htmlFor="cpn-redeem">
                  Redeem by
                </label>
                <input
                  id="cpn-redeem"
                  type="date"
                  className="cpn-input"
                  name="redeemBy"
                  value={form.redeemBy}
                  onChange={handleChange}
                />
                <span className="cpn-help">Optional expiry date</span>
              </div>
            </div>
          </DialogContent>

          <DialogActions
            sx={{ px: 3, py: 2.25, gap: 1.25, borderTop: "1px solid #eef0f4", background: "#fff" }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              style={{
                background: "#fff",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                padding: "10px 20px",
                fontWeight: 600,
                color: "#374151",
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <PrimaryButton type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create Coupon"}
            </PrimaryButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={() => deletingId || setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: "#fdecea",
              color: "#dc2626",
              marginBottom: 12,
            }}
          >
            <HiOutlineTrash size={26} />
          </span>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Delete coupon?</div>
          <div style={{ fontSize: 13.5, color: "#6b7280", marginTop: 8, lineHeight: 1.5 }}>
            This will permanently remove <strong>{confirmDelete?.code}</strong> from your database
            and from Stripe. Customers will no longer be able to redeem it. This action cannot be
            undone.
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.25, justifyContent: "center" }}>
          <button
            type="button"
            onClick={() => setConfirmDelete(null)}
            disabled={Boolean(deletingId)}
            style={{
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 10,
              padding: "10px 20px",
              fontWeight: 600,
              color: "#374151",
              cursor: deletingId ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={Boolean(deletingId)}
            style={{
              background: "#dc2626",
              border: "none",
              borderRadius: 10,
              padding: "10px 20px",
              fontWeight: 600,
              color: "#fff",
              cursor: deletingId ? "not-allowed" : "pointer",
              opacity: deletingId ? 0.7 : 1,
            }}
          >
            {deletingId ? "Deleting…" : "Delete"}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Coupens;
